class ProductQuickView extends HTMLElement {
  constructor() {
    super();
    this.resizeObserver = null;
    this.onResize = this.syncPanelHeight.bind(this);
  }

  connectedCallback() {
    // Re-initialize Swiper
    const newProductImages = this.querySelector('product-images');
    if (newProductImages && typeof newProductImages.initSwiper === 'function') {
      // Let JS event loop process the custom element upgrade first
      setTimeout(() => {
        newProductImages.initSwiper();
      }, 50);
    }

    this.setupPanelSync();
    this.setupScrollIndicator();
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onResize);

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    this.cleanupScrollIndicator();
  }

  setupPanelSync() {
    const mediaPanel = this.querySelector('[data-qv-transition-image]');
    const infoPanel = this.querySelector('.product-quick-view__panel');

    if (!mediaPanel || !infoPanel) return;

    this.syncPanelHeight();
    window.addEventListener('resize', this.onResize, { passive: true });

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => {
        this.syncPanelHeight();
      });
      this.resizeObserver.observe(mediaPanel);
    }

    const images = mediaPanel.querySelectorAll('img');
    images.forEach((image) => {
      if (image.complete) return;
      image.addEventListener('load', this.onResize, { once: true });
    });
  }

  syncPanelHeight() {
    const mediaPanel = this.querySelector('[data-qv-transition-image]');
    const infoPanel = this.querySelector('.product-quick-view__panel');

    if (!mediaPanel || !infoPanel) return;

    if (window.matchMedia('(min-width: 768px)').matches) {
      const mediaHeight = Math.round(mediaPanel.getBoundingClientRect().height);
      if (mediaHeight > 0) {
        const syncedHeight = `${mediaHeight}px`;
        infoPanel.style.height = syncedHeight;
        infoPanel.style.maxHeight = syncedHeight;
      }
      return;
    }

    infoPanel.style.removeProperty('height');
    infoPanel.style.removeProperty('max-height');
  }

  setupScrollIndicator() {
    const contentEl = this.querySelector('.product-quick-view__content');
    if (!contentEl) return;

    const indicator = contentEl.querySelector('.js-qv-scroll-indicator');
    if (!indicator) return;

    const btn = indicator.querySelector('.js-qv-scroll-btn');
    let dismissed = false;

    const dismiss = () => {
      if (dismissed) return;
      dismissed = true;
      indicator.classList.add('hidden');
    };

    const onScroll = () => dismiss();
    const onClick = () => {
      dismiss();
      contentEl.scrollBy({ top: 100, behavior: 'smooth' });
    };

    contentEl.addEventListener('scroll', onScroll, {
      passive: true,
      once: true,
    });
    if (btn) btn.addEventListener('click', onClick);

    const ro = new ResizeObserver(() => {
      if (!dismissed) this.updateScrollIndicator(contentEl, indicator);
    });
    ro.observe(contentEl);

    this.updateScrollIndicator(contentEl, indicator);

    this._scrollIndicatorCleanup = () => {
      contentEl.removeEventListener('scroll', onScroll);
      if (btn) btn.removeEventListener('click', onClick);
      ro.disconnect();
    };
  }

  updateScrollIndicator(contentEl, indicator) {
    const style = getComputedStyle(contentEl);
    const scrollable =
      (style.overflowY === 'auto' || style.overflowY === 'scroll') &&
      contentEl.scrollHeight > contentEl.clientHeight + 10;
    indicator.classList.toggle('hidden', !scrollable);
  }

  cleanupScrollIndicator() {
    if (this._scrollIndicatorCleanup) {
      this._scrollIndicatorCleanup();
      this._scrollIndicatorCleanup = null;
    }
  }
}
customElements.define('product-quick-view', ProductQuickView);

function trackQuickViewItem(quickView) {
  const itemId =
    quickView.dataset.analyticsVariantId || quickView.dataset.analyticsProductId;
  const price = Number(quickView.dataset.analyticsPrice || 0);

  if (!itemId || !quickView.dataset.analyticsProductName) {
    return;
  }

  const payload = {
    currency: quickView.dataset.analyticsCurrency,
    value: price,
    items: [
      {
        item_id: itemId,
        item_name: quickView.dataset.analyticsProductName,
        item_brand: quickView.dataset.analyticsProductBrand,
        item_variant: quickView.dataset.analyticsVariantName,
        price,
        quantity: 1,
      },
    ],
    item_view_surface: 'quick_view',
  };

  const publish = () =>
    window.LenszaAnalytics?.publish('view_item', payload, {
      gaEventName: 'view_item',
    });

  if (typeof window.LenszaAnalytics?.publish === 'function') {
    publish();
    return;
  }

  document.addEventListener('lensza:analytics-ready', publish, { once: true });
}

function getQuickViewTransitionImage(link) {
  const quickView = link.closest('product-quick-view');
  if (!quickView) return null;

  const imageRoot = quickView.querySelector('[data-qv-transition-image]');
  if (!imageRoot) return null;

  // Prefer the currently visible main slider image, not hidden thumbs.
  const activeMainImage = imageRoot.querySelector(
    'product-images .js-product-main-swiper .swiper-slide-active img',
  );
  if (activeMainImage) {
    const rect = activeMainImage.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      return activeMainImage;
    }
  }

  // Fallback: first visible image within main slider.
  const mainImages = imageRoot.querySelectorAll(
    'product-images .js-product-main-swiper .swiper-slide img',
  );
  for (const image of mainImages) {
    const rect = image.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      return image;
    }
  }

  // Last fallback.
  return imageRoot.querySelector('img');
}

// Initialize global trigger listeners
document.addEventListener('click', (event) => {
  const viewDetailsLink = event.target.closest('[data-qv-view-details]');
  if (viewDetailsLink) {
    document.dispatchEvent(
      new CustomEvent('product-quick-view:navigate', { bubbles: false }),
    );

    const transitionApi = window.PdpNavigationTransition;
    const dialogModal = document.getElementById('DialogModal');

    if (transitionApi) {
      const transitionImage = getQuickViewTransitionImage(viewDetailsLink);
      transitionApi.start({
        event,
        link: viewDetailsLink,
        image: transitionImage,
      });
    }

    if (dialogModal && dialogModal.open) {
      if (typeof dialogModal.close === 'function') {
        dialogModal.close();
      }
    }

    return;
  }

  const trigger = event.target.closest('.js-quick-view-trigger');
  if (!trigger) return;

  event.preventDefault();
  event.stopPropagation();

  const productUrl = trigger.dataset.productUrl;
  const dialogModal = document.getElementById('DialogModal');

  if (productUrl && dialogModal) {
    const defaultContent = trigger.querySelector('.js-quick-view-default');
    const loadingContent = trigger.querySelector('.js-quick-view-loading');

    // Lock button width so it does not shrink when only the loading icon is visible.
    const triggerWidth = Math.ceil(trigger.getBoundingClientRect().width);
    trigger.style.width = `${triggerWidth}px`;
    trigger.disabled = true;
    trigger.setAttribute('aria-busy', 'true');

    if (defaultContent) {
      defaultContent.classList.add('hidden');
    }

    if (loadingContent) {
      loadingContent.classList.remove('hidden');
      loadingContent.classList.add('inline-flex');
    }

    // Fetch the product-quick-view section while preserving existing query params
    const fetchUrl = new URL(productUrl, window.location.origin);
    fetchUrl.searchParams.set('section_id', 'product-quick-view');
    if (trigger.dataset.variantId) {
      fetchUrl.searchParams.set('variant', trigger.dataset.variantId);
    }

    fetch(fetchUrl.toString())
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');

        // Get the innerHTML of the returned section to exclude the Shopify section wrapper
        const sectionContent = html.getElementById(
          'shopify-section-product-quick-view',
        );

        if (sectionContent) {
          dialogModal.setContent(sectionContent.innerHTML);
          dialogModal.openModal();
          const quickView = dialogModal.querySelector('product-quick-view');
          if (quickView) {
            trackQuickViewItem(quickView);
          }
        } else {
          throw new Error('Could not find section content');
        }
      })
      .catch((error) => {
        console.error('Error fetching quick view:', error);
        dialogModal.setContent(`
            <div class="p-8 text-center text-red-500">
              <p>Sorry, there was an error loading the product details.</p>
            </div>
          `);
        dialogModal.openModal();
      })
      .finally(() => {
        // Restore trigger button
        if (defaultContent) {
          defaultContent.classList.remove('hidden');
        }

        if (loadingContent) {
          loadingContent.classList.remove('inline-flex');
          loadingContent.classList.add('hidden');
        }

        trigger.disabled = false;
        trigger.removeAttribute('aria-busy');
        trigger.style.removeProperty('width');
      });
  }
});
